// src/components/Eventos/EventStatusControl.jsx
import React, { useMemo, useState } from 'react';
import {
  Button, Chip, Stack, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions,
  useMediaQuery, useTheme, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import PlayArrowIcon  from '@mui/icons-material/PlayArrow';
import FlagIcon       from '@mui/icons-material/Flag';
import CheckIcon      from '@mui/icons-material/CheckCircle';
import HourglassTop   from '@mui/icons-material/HourglassTop';

import {
  useEventContext,
  TrackEventCodes,
  fieldHorizontalEvents,
  fieldVerticalEvents,
} from '../../context/EventContext';

export default function EventStatusControl({ heatDetails, unifiedStyles, inline = false }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /* ───────────────── context ───────────────── */
  const {
    participantsResults,
    setHeatStatus,
    isHeatComplete,
    setResultStatus,
  } = useEventContext();

  /* ───────────────── datos del heat ───────────────── */
  const key        = `${heatDetails.eventNumber}_${heatDetails.round}_${heatDetails.heat}`;
  const res        = participantsResults[key] || {};
  const status     = res.status    || 'not_started';   // 'not_started' | 'in_progress' | 'finished'
  const startTime  = res.startTime?.toDate?.() ?? null;
  const endTime    = res.endTime?.toDate?.()   ?? null;
  const resultStatus  = res.resultStatus  || 'unofficial';

  /* ───────────────── tipo de evento ───────────────── */
  const eventType = useMemo(() => {
    const c = heatDetails.eventCode;
    if (TrackEventCodes.includes(c))           return 'track';
    if (fieldHorizontalEvents.includes(c))     return 'horizontal';
    if (fieldVerticalEvents.includes(c))       return 'vertical';
    return 'unknown';
  }, [heatDetails.eventCode]);

  const complete = useMemo(
    () => isHeatComplete(heatDetails),
    [heatDetails, participantsResults]   // ← nuevas dependencias
  );

  /* ───────────────── estado UI ───────────────── */
  const [warnOpen, setWarnOpen] = useState(false);
  const [busy,     setBusy]     = useState(false);

  /* ───────────────── handlers ───────────────── */
  const startHeat = async () => {
    setBusy(true);
    await setHeatStatus(heatDetails.eventNumber, heatDetails.round, heatDetails.heat, 'in_progress');
    setBusy(false);
  };

  const finishHeat = async () => {
    if (!complete) {            // faltan resultados → preguntar
      setWarnOpen(true);
    } else {
      await forceFinish();
    }
  };

  const forceFinish = async () => {
    setBusy(true);
    await setHeatStatus(heatDetails.eventNumber, heatDetails.round, heatDetails.heat, 'finished');
    setBusy(false);
    setWarnOpen(false);
  };

  const handleResultChange = (e) => {
    setResultStatus(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      e.target.value
    );
  };

  /* ───────────────── render ───────────────── */
  if (inline) {
    // Modo inline: retorna solo el botón/chip sin Stack wrapper
    if (status === 'not_started') {
      return (
        <Button
          variant="contained"
          color="success"
          startIcon={<PlayArrowIcon />}
          disabled={busy}
          onClick={startHeat}
          fullWidth={isMobile}
          sx={unifiedStyles}
        >
          Iniciar competencia
        </Button>
      );
    }

    if (status === 'in_progress') {
      return (
        <Button
          variant="contained"
          color="error"
          startIcon={<FlagIcon />}
          disabled={busy}
          onClick={finishHeat}
          fullWidth={isMobile}
          sx={unifiedStyles}
        >
          Terminar competencia
        </Button>
      );
    }

    if (status === 'finished') {
      return (
        <Chip
          icon={<CheckIcon />}
          label={`Finalizado • ${endTime?.toLocaleTimeString() || ''}`}
          color="primary"
          variant="outlined"
          sx={{
            ...(unifiedStyles || {}),
            cursor: 'default',
            '&:hover': {
              transform: 'none',
            },
          }}
        />
      );
    }

    return null; // No hay botón que mostrar
  }

  // Modo normal: con Stack wrapper
  return (
    <>
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={2} 
        alignItems="center" 
        mt={2} 
        flexWrap="wrap"
      >
        {status === 'not_started' && (
          <Button
            variant="contained"
            color="success"
            startIcon={<PlayArrowIcon />}
            disabled={busy}
            onClick={startHeat}
            fullWidth={isMobile}
            sx={unifiedStyles}
          >
            Iniciar competencia
          </Button>
        )}

        {status === 'in_progress' && (
          <Button
            variant="contained"
            color="error"
            startIcon={<FlagIcon />}
            disabled={busy}
            onClick={finishHeat}
            fullWidth={isMobile}
            sx={unifiedStyles}
          >
            Terminar competencia
          </Button>
        )}

        {status === 'finished' && (
          <>
            <Chip
              icon={<CheckIcon />}
              label={`Finalizado • ${endTime?.toLocaleTimeString() || ''}`}
              color="primary"
              variant="outlined"
              sx={{
                ...(unifiedStyles || {}),
                cursor: 'default',
                '&:hover': { transform: 'none' },
              }}
            />

            <FormControl size="small" sx={{ minWidth: 180, mt: 1 }}>
              <InputLabel>Resultado</InputLabel>
              <Select
                value={resultStatus}
                label="Resultado"
                onChange={handleResultChange}
              >
                <MenuItem value="unofficial">No oficial</MenuItem>
                <MenuItem value="official">Oficial</MenuItem>
                <MenuItem value="under_protest">Bajo protesta</MenuItem>
              </Select>
            </FormControl>
          </>
        )}

        {startTime && status !== 'finished' && (
          <Chip
            size="small"
            icon={<HourglassTop />}
            label={`Inicio • ${startTime.toLocaleTimeString()}`}
            variant="outlined"
          />
        )}
      </Stack>

      {/* ───── Modal de confirmación ───── */}
      <Dialog open={warnOpen} onClose={() => setWarnOpen(false)}>
        <DialogTitle>Faltan resultados</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Aún hay atletas sin marca válida o intentos vacíos.<br />
            ¿Deseas cerrar el heat de todos modos?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarnOpen(false)}>Seguir editando</Button>
          <Button color="error" onClick={forceFinish} autoFocus>
            Terminar de todas formas
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}